import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/glitch-ui/data-table';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { useAdPerformanceReportQuery, useCampaignStatusReportQuery, metaCampaignOverviewQueryOptions, metaIntegrationStatusQueryOptions } from '@/lib/query-options';
import { formatMoney, toDisplayAmount } from '@/lib/performance/money';
import { deriveMetrics, formatRate, formatRoas } from '@/lib/performance/metrics';
import { pivotLeaves, availableDimensions, PIVOT_DIMENSIONS, type PivotDimension, type PivotLeaf, type PivotRow } from '@/lib/performance/pivot';
import { resolveDateWindow } from '@/lib/performance/date-window';
import { platformsInScope, type Platform, type PlatformScope } from '@/lib/performance/types';
import { PlatformLogo } from '@/lib/performance/platform';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { usePerformanceData, useScopedCampaigns } from '@/hooks/usePerformanceData';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { ColumnsPopover, ImpressionShareCell, LostIsCell, OptScorePill, BidStrategyCell, CampaignStatePill, MutedDash } from '../cells';
import { SubViewSwitcher } from './sub-view-switcher';
import { CampaignsTab, type CampaignSubView } from '../tabs/campaigns-tab';
import {
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCustomRangeAtom,
  consolidatedPerformanceGroupByAtom,
  consolidatedPerformanceComparisonAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

// Column-picker metadata for the Campaigns / Ad-Group tables. Duplicated
// from perf-v2 (performance-v2.tsx:103-151) so the Google Campaigns view can
// drive the same column-visibility hook; de-dup tracked in #5994.
const CAMPAIGN_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean }[] = [
  { id: 'name',         label: 'Campaign',     pinned: true },
  { id: 'status',       label: 'Status' },
  { id: 'impressions',  label: 'Impressions' },
  { id: 'clicks',       label: 'Clicks' },
  { id: 'conversions',  label: 'Conversions' },
  { id: 'cost',         label: 'Cost' },
  { id: 'cpa',          label: 'CPA' },
  { id: 'roas',         label: 'ROAS' },
  { id: 'searchIS',     label: 'Search IS' },
  { id: 'lostIS',       label: 'Lost IS' },
  { id: 'optScore',     label: 'Opt. score' },
  { id: 'bidStrategy',  label: 'Bid strategy' },
];
const CAMPAIGN_COLUMN_IDS = new Set(CAMPAIGN_TABLE_COLUMNS.map(c => c.id));
const CAMPAIGN_PINNED_COLUMN_IDS = new Set(CAMPAIGN_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));

const AD_GROUP_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean }[] = [
  { id: 'adGroupName', label: 'Ad Group',    pinned: true },
  { id: 'adCount',     label: 'Ads count' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'clicks',      label: 'Clicks' },
  { id: 'ctr',         label: 'CTR' },
  { id: 'conversions', label: 'Conversions' },
  { id: 'cost',        label: 'Cost' },
  { id: 'cpa',         label: 'CPA' },
  { id: 'roas',        label: 'ROAS' },
];
const AD_GROUP_COLUMN_IDS = new Set(AD_GROUP_TABLE_COLUMNS.map(c => c.id));
const AD_GROUP_PINNED_COLUMN_IDS = new Set(AD_GROUP_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));

// 09-08 toolbar standardisation: every sub-view carries the Columns control,
// so the toolbar's right side never appears and disappears across views.
const TYPE_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean }[] = [
  { id: 'label',       label: 'Type', pinned: true },
  { id: 'campaigns',   label: 'Campaigns' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'clicks',      label: 'Clicks' },
  { id: 'conversions', label: 'Conversions' },
  { id: 'cost',        label: 'Cost' },
  { id: 'cpa',         label: 'CPA' },
  { id: 'roas',        label: 'ROAS' },
];
const TYPE_COLUMN_IDS = new Set(TYPE_TABLE_COLUMNS.map(c => c.id));
const TYPE_PINNED_COLUMN_IDS = new Set(TYPE_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));

const CONV_ACTION_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean }[] = [
  { id: 'conversion_action_name',  label: 'Conversion action', pinned: true },
  { id: 'conversions',             label: 'Conversions' },
  { id: 'all_conversions',         label: 'All conversions' },
  { id: 'all_conversions_value',   label: 'Conversion value' },
  { id: 'cost',                    label: 'Cost' },
  { id: 'cost_per_conv',           label: 'Cost per conversion' },
];
const CONV_ACTION_COLUMN_IDS = new Set(CONV_ACTION_TABLE_COLUMNS.map(c => c.id));
const CONV_ACTION_PINNED_COLUMN_IDS = new Set(CONV_ACTION_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));

// Column picker for the merged pivot (09-08): the superset of every id the
// pivot can render across scopes. Ids missing from this set fail OPEN (stay
// visible) so a new column can never vanish by omission.
const CAMPAIGN_PIVOT_COLUMN_IDS = new Set([
  'platform', 'label', 'impressions', 'clicks', 'results', 'spend', 'roas', 'cpc',
  'ctr', 'imprToConv', 'clickToConv', 'cpa', 'status', 'campaignType', 'frequency', 'verdicts',
  'searchIS', 'lostIS', 'optScore', 'bidStrategy',
]);
const CAMPAIGN_PIVOT_PINNED_IDS = new Set(['platform', 'label']);
// Default view = the volume-and-status read (ruling 09-09): Impressions,
// Clicks, Conversions, Cost, Status. Everything else — the derived ratios
// and the Google diagnostics — stays one Columns click away without
// widening the default table.
const CAMPAIGN_PIVOT_DEFAULT_VISIBLE = new Set([
  'platform', 'label', 'impressions', 'clicks', 'results', 'spend', 'status',
  // Meta-native columns keep their meta-scope default (they only render there).
  'campaignType', 'frequency', 'verdicts',
]);
const pivotColId = (c: ColumnDef<PivotRow, unknown>): string =>
  (c.id ?? (c as { accessorKey?: string }).accessorKey) as string;

// perf-v2 DEFAULT_LAYOUTS.campaigns tab id under the consolidated shell's
// shared widget-layout atom (see CONSOLIDATED_DEFAULT_LAYOUTS).
const GOOGLE_CAMPAIGNS_LAYOUT_KEY = 'google-campaigns';

// Meta-native campaign attributes not carried on the generic pivot (#6057) — looked up
// by the campaign's pivot key (`meta:<id>`) when grouping by Campaign under Meta scope.
interface MetaCampaignExtras {
  campaignType: string | null;
  frequency: number | null;
  winnerCount: number;
  loserCount: number;
}

function numOrNull(value: string | null | undefined): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Unified Campaigns table (#5971, CP-7; #6040, T2). A dimension pivot: the same
 * ad-level (Google) + campaign-level (Meta) data grouped by a user-chosen
 * dimension (Platform / Campaign / Ad group), rendering the canonical metric block
 * (metrics.ts). Generalizes the old fixed campaign grouping into the shared pivot;
 * more dimensions (Ad, Angle, Location) plug into PIVOT_DIMENSIONS (T3 / #6037).
 */
export function CampaignsPanel({
  scope,
  forcedDimension,
  toolbarLeading,
}: {
  scope: PlatformScope;
  /** Unified-switcher hosting (09-08): the wrapper owns the dimension. */
  forcedDimension?: PivotDimension;
  toolbarLeading?: React.ReactNode;
}) {
  const inScope = platformsInScope(scope);
  const googleInScope = inScope.includes('google');
  const metaInScope = inScope.includes('meta');
  const { symbol } = useCurrencySymbol();
  const cs = symbol ?? '';

  // Honor the toolbar date range through the shared exact-day window (#6274).
  // Meta stays snapshot-backed (its own sync window).
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const window = useMemo(() => resolveDateWindow(selectedDays, customRange), [selectedDays, customRange]);

  // #6277 — the Google half of the merged pivot uses the SAME campaign scoping
  // as Google scope (it was account-wide `undefined`, so flipping All → Google
  // changed the Google numbers and the campaign filters were inert here).
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const { scopedCampaigns, scopedGoogleAdsIds } = useScopedCampaigns({ includeExternal, typeFilter, selectedCampaignIds });

  // #6278 — Meta money renders in the Meta AD ACCOUNT's currency (the Audience
  // panel's #6182 sourcing); one org can run differently-billed accounts.
  // Enabled travels through the factory (#6489) so its org-seeded gate holds.
  const { data: metaStatus } = useQuery(metaIntegrationStatusQueryOptions(metaInScope));
  const { symbol: metaSymbol } = useCurrencySymbol(undefined, metaStatus?.selected_ad_account?.currency);
  const metaCs = metaSymbol ?? cs;
  const symFor = (p: PivotRow['platform']) => (p === 'meta' ? metaCs : cs);


  const { data: adData, isLoading: googleLoading, isError: googleError } = useAdPerformanceReportQuery(
    scopedGoogleAdsIds || undefined,
    undefined,
    {
      startDate: window.start_date,
      endDate: window.end_date,
      // Disabled when the filter matches nothing — an absent campaign_ids
      // param means account-wide to the backend (#6275).
      enabled: googleInScope && scopedGoogleAdsIds.length > 0,
    },
  );
  const { data: metaResp, isLoading: metaLoading, isError: metaError } = useQuery(metaCampaignOverviewQueryOptions(metaInScope));

  // Per-campaign Google diagnostics (Search IS, Lost IS, opt score, bid
  // strategy) for the pivot's opt-in columns — the same #4300 report the
  // retired by-campaign view read, keyed here by the pivot's row key.
  const { data: campaignStatusData } = useCampaignStatusReportQuery(
    scopedGoogleAdsIds || '',
    window.start_date,
    window.end_date,
    { enabled: googleInScope && scopedGoogleAdsIds.length > 0 },
  );
  const campaignStatusByKey = useMemo(() => {
    const map = new Map<string, NonNullable<typeof campaignStatusData>[number]>();
    for (const row of campaignStatusData ?? []) {
      map.set(`google:${row.campaign_id}`, row);
    }
    return map;
  }, [campaignStatusData]);

  // Group-by dimensions available for this scope; clamp the selection when scope changes.
  // Platform grouping is intentionally omitted — the top-level scope switch (All/Google/
  // Meta) already serves that purpose, so offering it here would clash.
  const dimensions = useMemo(() => availableDimensions(scope, ['campaign', 'ad-group']), [scope]);
  const [groupBy, setGroupBy] = useState<PivotDimension>('campaign');
  const activeDimension = forcedDimension ?? (dimensions.includes(groupBy) ? groupBy : (dimensions[0] ?? 'campaign'));

  // Flatten both platforms into leaves — Google at ad level so ad-group grouping
  // works; Meta at campaign level (no ad-group, so those leaves drop out of the
  // ad-group pivot) — then group by the active dimension.
  const { leaves, extras } = useMemo(() => {
    const out: PivotLeaf[] = [];
    const ex = new Map<string, MetaCampaignExtras>();
    if (googleInScope) {
      // Glitch lifecycle state per Google campaign id, so REPORTED rows carry
      // a Status too (it was only filled for Meta and seeded rows, leaving
      // live Google campaigns dashed in a default column). External
      // campaigns have no Glitch record and keep the dash.
      const stateByGid = new Map<string, string>();
      for (const c of scopedCampaigns) {
        const gid = c.googleads_campaign?.googleads_campaign_id;
        if (gid != null && c.state) stateByGid.set(String(gid), c.state);
      }
      const reported = new Set<string>();
      for (const ad of adData ?? []) {
        reported.add(String(ad.campaign_id));
        out.push({
          platform: 'google',
          campaignId: ad.campaign_id,
          campaignName: ad.campaign_name || ad.campaign_id,
          adGroupId: ad.ad_group_id,
          adGroupName: ad.ad_group_name || ad.ad_group_id,
          status: stateByGid.get(String(ad.campaign_id)) ?? null,
          impressions: ad.impressions ?? 0,
          clicks: ad.clicks ?? 0,
          results: ad.conversions ?? 0,
          spend: ad.cost ?? 0,
          conversionValue: ad.all_conversions_value ?? 0,
        });
      }
      // Every scoped Glitch campaign gets a row even before it has served
      // (09-09): drafts and zero-history campaigns exist too, and "No data
      // with activity" wrongly implied they ran. Zero leaves seed only
      // campaigns the report did not cover; the Glitch lifecycle state fills
      // the Status column. A campaign's local ads seed its ad-group rows
      // (each Glitch ad becomes one Google ad group on push), so the Ad
      // Groups view lists the draft structure too.
      for (const c of scopedCampaigns) {
        const gid = c.googleads_campaign?.googleads_campaign_id;
        if (gid != null && reported.has(String(gid))) continue;
        const campaignId = gid != null ? String(gid) : `glitch-${c.id}`;
        const status = c.state ?? null;
        // Never been live (no Google Ads id, or still a draft): nothing was
        // ever measured, so the sums render "—" rather than measured zeros.
        const unmeasured = gid == null || c.state === 'draft';
        const base = {
          platform: 'google' as const,
          campaignId,
          campaignName: c.name,
          status,
          impressions: 0,
          clicks: 0,
          results: 0,
          spend: 0,
          conversionValue: null,
          unmeasured,
        };
        const ads = c.ads ?? [];
        if (ads.length === 0) {
          out.push({ ...base, adGroupId: null, adGroupName: null });
        } else {
          ads.forEach((ad, i) => {
            const agid = ad.googleads_ad_group?.googleads_ad_group_id;
            out.push({
              ...base,
              adGroupId: agid != null ? String(agid) : `glitch-ad-${ad.id}`,
              adGroupName: `Ad ${i + 1}`,
            });
          });
        }
      }
    }
    if (metaInScope && metaResp) {
      for (const m of metaResp.rows) {
        out.push({
          platform: 'meta',
          campaignId: m.meta_campaign_id,
          campaignName: m.name || m.meta_campaign_id,
          adGroupId: null,
          adGroupName: null,
          status: m.status ?? null,
          impressions: m.impressions,
          clicks: m.clicks,
          results: m.leads,
          spend: toDisplayAmount(m.spend_cents, 'meta'),
          conversionValue: null,
        });
        ex.set(`meta:${m.meta_campaign_id}`, {
          campaignType: m.campaign_type ?? null,
          frequency: numOrNull(m.frequency),
          winnerCount: m.winner_ad_count,
          loserCount: m.loser_ad_count,
        });
      }
    }
    return { leaves: out, extras: ex };
  }, [googleInScope, adData, scopedCampaigns, metaInScope, metaResp]);

  const rows = useMemo(() => pivotLeaves(leaves, activeDimension), [leaves, activeDimension]);

  const columns = useMemo<ColumnDef<PivotRow, unknown>[]>(() => {
    const cols: ColumnDef<PivotRow, unknown>[] = [];
    // Thin, sortable platform-logo column pinned to the left of the name — only under
    // All scope (mixed platforms). Under a single-platform scope the rows are already
    // filtered to one platform, so it would be noise.
    if (scope === 'all') {
      cols.push({
        id: 'platform',
        accessorFn: (r) => r.platform,
        header: '',
        cell: ({ row }) => <PlatformLogo platform={row.original.platform} className="h-4 w-4" />,
        meta: { className: 'pl-4 pr-1 w-8' },
      });
    }
    cols.push({
      accessorKey: 'label',
      header: PIVOT_DIMENSIONS[activeDimension].columnHeader,
      cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.label}</span>,
    });
    // A never-live row's sums are "—", never 0 (ruling 09-09): nothing was
    // measured. The derived ratios already dash via their null denominators.
    const dashIfUnmeasured = (row: PivotRow, render: () => React.ReactNode) =>
      row.unmeasured ? <MutedDash /> : render();
    cols.push(
      // Layer 2 — volume
      { accessorKey: 'impressions', header: 'Impr.', cell: ({ row }) => dashIfUnmeasured(row.original, () => row.original.impressions.toLocaleString()) },
      { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => dashIfUnmeasured(row.original, () => row.original.clicks.toLocaleString()) },
      {
        accessorKey: 'results',
        // Single-platform scope uses that platform's word; the mixed "All" view
        // shows Conversions (Google's term) with a per-row "leads" suffix on Meta rows.
        header: scope === 'meta' ? 'Leads' : 'Conv.',
        cell: ({ row }) =>
          dashIfUnmeasured(row.original, () =>
            scope === 'meta'
              ? Math.round(row.original.results).toLocaleString()
              : row.original.resultKind === 'leads'
                ? `${Math.round(row.original.results).toLocaleString()} leads`
                : Math.round(row.original.results).toLocaleString()),
      },
      // Layer 1 — money
      { accessorKey: 'spend', header: 'Cost', cell: ({ row }) => dashIfUnmeasured(row.original, () => formatMoney(row.original.spend, symFor(row.original.platform))) },
      { id: 'roas', accessorFn: (r) => deriveMetrics(r).roas, header: 'ROAS', cell: ({ row }) => formatRoas(deriveMetrics(row.original).roas) },
      { id: 'cpc', accessorFn: (r) => deriveMetrics(r).cpc, header: 'CPC', cell: ({ row }) => formatMoney(deriveMetrics(row.original).cpc, symFor(row.original.platform)) },
      // Layer 3 — rates
      { id: 'ctr', accessorFn: (r) => deriveMetrics(r).ctr, header: 'CTR', cell: ({ row }) => formatRate(deriveMetrics(row.original).ctr, 2) },
      { id: 'imprToConv', accessorFn: (r) => deriveMetrics(r).imprToConv, header: 'Impr→Conv.', cell: ({ row }) => formatRate(deriveMetrics(row.original).imprToConv, 2) },
      { id: 'clickToConv', accessorFn: (r) => deriveMetrics(r).clickToConv, header: 'Conv. rate', cell: ({ row }) => formatRate(deriveMetrics(row.original).clickToConv, 1) },
      // Efficiency
      {
        id: 'cpa',
        accessorFn: (r) => (r.results > 0 ? r.spend / r.results : null),
        header: scope === 'meta' ? 'Cost / lead' : 'Cost / conv.',
        cell: ({ row }) => (row.original.results > 0 ? formatMoney(row.original.spend / row.original.results, symFor(row.original.platform)) : '—'),
      },
    );
    // Status is only meaningful per-campaign (Meta carries it); omit for other pivots.
    if (activeDimension === 'campaign') {
      // Pilled like the perf-v2 Campaigns table (09-09); Meta statuses
      // (ACTIVE / PAUSED / ARCHIVED) lowercase into the same style map.
      cols.push({
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <CampaignStatePill state={row.original.status?.toLowerCase() ?? null} />,
      });
      // Google campaign diagnostics (#4300 parity, 09-09): joined from the
      // campaign-status report; Meta rows have no counterpart and dash.
      if (googleInScope) {
        const statusFor = (key: string) => campaignStatusByKey.get(key);
        cols.push(
          { id: 'searchIS', header: 'Search IS', cell: ({ row }) =>
            row.original.platform === 'meta' ? <MutedDash /> : <ImpressionShareCell value={statusFor(row.original.key)?.search_impression_share ?? null} /> },
          { id: 'lostIS', header: 'Lost IS', cell: ({ row }) =>
            row.original.platform === 'meta' ? <MutedDash /> : (
              <LostIsCell
                budget={statusFor(row.original.key)?.search_budget_lost_impression_share ?? null}
                rank={statusFor(row.original.key)?.search_rank_lost_impression_share ?? null}
              />
            ) },
          { id: 'optScore', header: 'Opt. score', cell: ({ row }) =>
            row.original.platform === 'meta' ? <MutedDash /> : <OptScorePill score={statusFor(row.original.key)?.optimization_score ?? null} /> },
          { id: 'bidStrategy', header: 'Bid strategy', cell: ({ row }) =>
            row.original.platform === 'meta' ? <MutedDash /> : <BidStrategyCell strategy={statusFor(row.original.key)?.bidding_strategy_type ?? null} /> },
        );
      }
      // Meta-native campaign attributes (#6057) — only under Meta scope; Google campaigns
      // have no type / frequency / winner-loser verdicts, so they'd be blank under All.
      if (scope === 'meta') {
        cols.push(
          {
            id: 'campaignType',
            header: 'Type',
            cell: ({ row }) => {
              const t = extras.get(row.original.key)?.campaignType;
              return t ? <span className="capitalize text-gray-700">{t.toLowerCase()}</span> : '—';
            },
          },
          {
            id: 'frequency',
            header: 'Freq.',
            cell: ({ row }) => {
              const f = extras.get(row.original.key)?.frequency;
              return f != null ? f.toFixed(2) : '—';
            },
          },
          {
            id: 'verdicts',
            header: 'W / L',
            cell: ({ row }) => {
              const e = extras.get(row.original.key);
              const w = e?.winnerCount ?? 0;
              const l = e?.loserCount ?? 0;
              return w === 0 && l === 0 ? (
                '—'
              ) : (
                <span>
                  <span className="font-medium text-green-600">{w}</span> / <span className="font-medium text-red-600">{l}</span>
                </span>
              );
            },
          },
        );
      }
    }
    return cols;
  }, [cs, metaCs, scope, activeDimension, extras, googleInScope, campaignStatusByKey]);

  const {
    visible: visiblePivotColumns, toggle: togglePivotColumn, reset: resetPivotColumns,
  } = useColumnVisibility({
    settingsKey: 'campaign_pivot',
    allIds: CAMPAIGN_PIVOT_COLUMN_IDS,
    pinnedIds: CAMPAIGN_PIVOT_PINNED_IDS,
    defaultVisibleIds: CAMPAIGN_PIVOT_DEFAULT_VISIBLE,
  });
  const shownColumns = useMemo(
    () =>
      columns.filter((c) => {
        const id = pivotColId(c);
        return !CAMPAIGN_PIVOT_COLUMN_IDS.has(id) || CAMPAIGN_PIVOT_PINNED_IDS.has(id) || visiblePivotColumns.has(id);
      }),
    [columns, visiblePivotColumns],
  );
  // Built from the rendered columns so labels track scope and dimension;
  // terse headers get spelled-out picker names.
  const pivotColumnsMeta = useMemo(
    () =>
      columns.map((c) => {
        const id = pivotColId(c);
        const friendly: Record<string, string> = {
          platform: 'Platform',
          frequency: 'Frequency',
          verdicts: 'Winners / Losers',
          imprToConv: 'Impressions to conversions',
          searchIS: 'Search impression share',
          lostIS: 'Lost impression share',
          optScore: 'Optimisation score',
        };
        return {
          id,
          label: friendly[id] ?? (typeof c.header === 'string' && c.header ? c.header : id),
          pinned: CAMPAIGN_PIVOT_PINNED_IDS.has(id),
        };
      }),
    [columns],
  );
  // The picker and its bubble count against the columns PRESENT at this
  // scope/dimension, not the cross-scope superset the visibility set spans.
  const presentPivotIds = useMemo(
    () => new Set(columns.map(pivotColId).filter((id) => CAMPAIGN_PIVOT_COLUMN_IDS.has(id))),
    [columns],
  );
  const visiblePresentIds = useMemo(
    () => new Set([...presentPivotIds].filter((id) => visiblePivotColumns.has(id))),
    [presentPivotIds, visiblePivotColumns],
  );

  const loading = (googleInScope && googleLoading) || (metaInScope && metaLoading);
  if (loading) {
    return <div className="flex h-24 items-center justify-center text-sm text-gray-400">Loading…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {toolbarLeading}
        {!forcedDimension && dimensions.length > 1 && (
          <SubViewSwitcher
            items={dimensions.map((d) => ({ id: d, label: PIVOT_DIMENSIONS[d].label }))}
            active={activeDimension}
            onChange={setGroupBy}
          />
        )}
        <div className="ml-auto">
          <ColumnsPopover
            columnsMeta={pivotColumnsMeta}
            visibleIds={visiblePresentIds}
            totalIds={presentPivotIds}
            onToggle={togglePivotColumn}
            onReset={resetPivotColumns}
          />
        </div>
      </div>
      <DataTable
        data={rows}
        columns={shownColumns}
        pageSize={15}
        emptyState={
          // #6496 — a failed report must not read as an empty account.
          (googleInScope && googleError) || (metaInScope && metaError)
            ? 'The report could not be loaded. Refresh to retry.'
            : metaInScope && !googleInScope
              // A never-synced integration is not the same truth as a synced
              // one with zero activity (09-08): say which it is.
              ? metaResp && metaResp.window == null
                ? 'Meta insights have not synced yet. Campaigns appear after the first sync.'
                : 'No Meta campaigns with activity in the synced window.'
              : 'No data with activity in this period'
        }
      />
    </div>
  );
}

/**
 * The Google-specific Campaigns view, reusing perf-v2's CampaignsTab verbatim.
 * Fed by the shared usePerformanceData hook (the same data layer /performance-v2
 * uses) so it honors the toolbar filters (#5995) with no duplicated computation.
 * Owns the sub-view toggle + column pickers locally; the Saved Views popover is
 * intentionally omitted for now.
 */
function GoogleCampaignsSection({
  getTabLayout,
  onReorder,
  onResize,
  forcedSubView,
  toolbarLeading,
}: {
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
  /** Unified-switcher hosting (09-08): the wrapper owns the sub-view; this
      section renders exactly that view, keeping its Columns control. */
  forcedSubView?: CampaignSubView;
  toolbarLeading?: React.ReactNode;
}) {
  const { symbol } = useCurrencySymbol('google');
  const cs = symbol ?? '';

  const [ownSubView, setSubView] = useState<CampaignSubView>('by-type');
  const subView = forcedSubView ?? ownSubView;

  const {
    visible: visibleCampaignColumns, toggle: toggleCampaignColumn, reset: resetCampaignColumns,
  } = useColumnVisibility({ settingsKey: 'campaigns', allIds: CAMPAIGN_COLUMN_IDS, pinnedIds: CAMPAIGN_PINNED_COLUMN_IDS });
  const {
    visible: visibleAdGroupColumns, toggle: toggleAdGroupColumn, reset: resetAdGroupColumns,
  } = useColumnVisibility({ settingsKey: 'ad_groups', allIds: AD_GROUP_COLUMN_IDS, pinnedIds: AD_GROUP_PINNED_COLUMN_IDS });
  const {
    visible: visibleTypeColumns, toggle: toggleTypeColumn, reset: resetTypeColumns,
  } = useColumnVisibility({ settingsKey: 'campaign_types', allIds: TYPE_COLUMN_IDS, pinnedIds: TYPE_PINNED_COLUMN_IDS });
  const {
    visible: visibleConvActionColumns, toggle: toggleConvActionColumn, reset: resetConvActionColumns,
  } = useColumnVisibility({ settingsKey: 'conversion_actions', allIds: CONV_ACTION_COLUMN_IDS, pinnedIds: CONV_ACTION_PINNED_COLUMN_IDS });

  // Driven by the shared filter toolbar (#5971/#5995).
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const groupBy = useAtomValue(consolidatedPerformanceGroupByAtom);
  const comparison = useAtomValue(consolidatedPerformanceComparisonAtom);
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);

  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);

  const perf = usePerformanceData({
    includeExternal,
    tab: 'campaigns',
    campaignSubView: subView,
    comparisonMode: comparison,
    selectedCampaignIds,
    chartDateRange: selectedDays,
    customRange,
    chartGroupBy: groupBy,
    showUserEdits: false,
    selectedMetric: 'conversions',
    selectedDays,
    typeFilter,
    cs,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {toolbarLeading}
        {!forcedSubView && (
          <SubViewSwitcher
            items={[
              { id: 'by-type' as const, label: 'By Type' },
              { id: 'by-campaign' as const, label: 'By Campaign' },
              { id: 'by-ad-group' as const, label: 'By Ad Group' },
              { id: 'by-conversion-action' as const, label: 'By Conv. Action' },
            ]}
            active={subView}
            onChange={setSubView}
          />
        )}
        {/* Right-aligned like every table toolbar control; every sub-view
            carries it so the toolbar never shifts between views (09-08). */}
        <div className="ml-auto">
          {subView === 'by-type' && (
            <ColumnsPopover
              columnsMeta={TYPE_TABLE_COLUMNS}
              visibleIds={visibleTypeColumns}
              totalIds={TYPE_COLUMN_IDS}
              onToggle={toggleTypeColumn}
              onReset={resetTypeColumns}
            />
          )}
          {subView === 'by-campaign' && (
            <ColumnsPopover
              columnsMeta={CAMPAIGN_TABLE_COLUMNS}
              visibleIds={visibleCampaignColumns}
              totalIds={CAMPAIGN_COLUMN_IDS}
              onToggle={toggleCampaignColumn}
              onReset={resetCampaignColumns}
            />
          )}
          {subView === 'by-ad-group' && (
            <ColumnsPopover
              columnsMeta={AD_GROUP_TABLE_COLUMNS}
              visibleIds={visibleAdGroupColumns}
              totalIds={AD_GROUP_COLUMN_IDS}
              onToggle={toggleAdGroupColumn}
              onReset={resetAdGroupColumns}
            />
          )}
          {subView === 'by-conversion-action' && (
            <ColumnsPopover
              columnsMeta={CONV_ACTION_TABLE_COLUMNS}
              visibleIds={visibleConvActionColumns}
              totalIds={CONV_ACTION_COLUMN_IDS}
              onToggle={toggleConvActionColumn}
              onReset={resetConvActionColumns}
            />
          )}
        </div>
      </div>
      <CampaignsTab
        subView={subView}
        visibleTypeColumns={visibleTypeColumns}
        visibleConversionActionColumns={visibleConvActionColumns}
        typeMetrics={perf.typeMetrics}
        filteredCampaigns={perf.filteredCampaigns}
        adGroupMetrics={perf.adGroupMetrics}
        conversionActions={perf.conversionActionsData}
        isConversionActionsLoading={perf.isConversionActionsLoading}
        campaignStatusData={perf.campaignStatusData}
        campaignStatusByGoogleId={perf.campaignStatusByGoogleId}
        previousCampaignTotalsById={perf.previousCampaignTotalsById}
        activeCampaigns={perf.activeCampaigns}
        visibleCampaignColumns={visibleCampaignColumns}
        visibleAdGroupColumns={visibleAdGroupColumns}
        currencySymbol={cs}
        dateRangeLabel={perf.dateRangeLabel}
        dateRangeFilter={perf.dateRangeFilter}
        layout={getTabLayout(GOOGLE_CAMPAIGNS_LAYOUT_KEY)}
        onReorder={(l) => onReorder(GOOGLE_CAMPAIGNS_LAYOUT_KEY, l)}
        onResize={(id, w) => onResize(GOOGLE_CAMPAIGNS_LAYOUT_KEY, id, w)}
      />
    </div>
  );
}

/**
 * Consolidated Campaigns tab (#5995) — hybrid by scope. Google renders the
 * full-depth perf-v2 CampaignsTab (groupings, diagnostics, column pickers) fed
 * by the shared usePerformanceData hook; Meta / All keep the existing merged
 * cross-platform CampaignsPanel table unchanged.
 */
export function ConsolidatedCampaignsPanel({
  scope,
  getTabLayout,
  onReorder,
  onResize,
}: {
  scope: PlatformScope;
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  // One always-visible switcher listing every view (ruling 09-08, the
  // Audience pattern she pointed at): each view's headers never change —
  // scope only filters rows. Google-native views appear when Google is in
  // scope, marked whenever the scope mixes platforms. "Ad Groups" was
  // removed (ruling 09-09): Glitch ads map 1:1 to Google ad groups, so the
  // view duplicated Creative & Ads under vocabulary the product never uses;
  // the pivot dimension stays dormant underneath.
  const [view, setView] = useState<'campaign' | 'by-type' | 'by-conversion-action'>('campaign');
  const inScope = platformsInScope(scope);
  const googleInScope = inScope.includes('google');
  const mark = inScope.length > 1;
  const items: { id: typeof view; label: string; platform?: Platform }[] = [
    { id: 'campaign', label: 'Campaigns' },
    ...(googleInScope
      ? ([
          { id: 'by-type' as const, label: 'By Type', platform: mark ? ('google' as const) : undefined },
          { id: 'by-conversion-action' as const, label: 'By Conv. Action', platform: mark ? ('google' as const) : undefined },
        ])
      : []),
  ];
  const activeView = items.some((i) => i.id === view) ? view : 'campaign';
  const switcher = items.length > 1 ? <SubViewSwitcher items={items} active={activeView} onChange={setView} /> : null;

  if (activeView === 'by-type' || activeView === 'by-conversion-action') {
    return (
      <GoogleCampaignsSection
        getTabLayout={getTabLayout}
        onReorder={onReorder}
        onResize={onResize}
        forcedSubView={activeView}
        toolbarLeading={switcher}
      />
    );
  }
  return (
    <CampaignsPanel
      scope={scope}
      forcedDimension="campaign"
      toolbarLeading={switcher}
    />
  );
}
